import { z } from 'zod';

export const webSocketFrameSchema = z.object({
  id: z.string(),
  seq: z.number().optional(),
  timestamp: z.string().optional(),
  direction: z.enum(['inbound', 'outbound']),
  encoding: z.enum(['utf8', 'base64']).optional(),
  byteSize: z.number().optional(),
  truncated: z.boolean().optional(),
  data: z.string(),
});

export type WebSocketFrame = z.infer<typeof webSocketFrameSchema>;

export const webSocketHandshakeSchema = z.object({
  id: z.string(),
  outcome: z.enum(['accepted', 'rejected', 'error']),
  startedAt: z.string().optional(),
  durationMs: z.number().optional(),
  request: z.object({
    method: z.string(),
    url: z.string(),
    headers: z.record(z.string(), z.string()),
    protocols: z.array(z.string()).optional(),
  }),
  response: z
    .object({
      status: z.number(),
      statusText: z.string().optional(),
      headers: z.record(z.string(), z.string()),
      body: z.string().optional(),
    })
    .optional(),
  error: z.string().optional(),
});

export type WebSocketHandshake = z.infer<typeof webSocketHandshakeSchema>;

export const webSocketEvidencePayloadSchema = z.object({
  sessionId: z.string(),
  url: z.string(),
  handshake: webSocketHandshakeSchema.optional(),
  frames: z.array(webSocketFrameSchema),
});

export type WebSocketEvidencePayload = z.infer<
  typeof webSocketEvidencePayloadSchema
>;

export function parseWebSocketEvidencePayload(
  output: unknown,
): WebSocketEvidencePayload | undefined {
  const candidate = normalizeOutput(output);

  if (candidate === undefined) {
    return undefined;
  }

  const parsed = webSocketEvidencePayloadSchema.safeParse(candidate);

  if (!parsed.success) {
    return undefined;
  }

  if (parsed.data.frames.length === 0 && !parsed.data.handshake) {
    return undefined;
  }

  return parsed.data;
}

function normalizeOutput(output: unknown): unknown {
  if (typeof output !== 'string') {
    return output;
  }

  const trimmed = output.trim();

  if (!trimmed) {
    return undefined;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}
